import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'mongodb',
  version: '1.0.0',
  properties: [
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      description: 'Select the MongoDB credential to use',
      credentialTypes: [{ name: 'mongodb', required: true }],
    },
    {
      key: 'database',
      label: 'Database',
      type: 'string',
      required: true,
      placeholder: 'myDatabase',
      description: 'Name of the MongoDB database',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getDatabases',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'find',
      options: [
        { value: 'find', name: 'Find', description: 'Find documents in a collection' },
        { value: 'findOne', name: 'Find One', description: 'Find a single document' },
        { value: 'insert', name: 'Insert', description: 'Insert a document' },
        { value: 'update', name: 'Update', description: 'Update documents' },
        { value: 'delete', name: 'Delete', description: 'Delete documents' },
        { value: 'aggregate', name: 'Aggregate', description: 'Run an aggregation pipeline' },
        { value: 'bulkInsert', name: 'Bulk Insert', description: 'Insert all documents from an array' },
        { value: 'createCollection', name: 'Create Collection', description: 'Create a new collection' },
        { value: 'dropCollection', name: 'Drop Collection', description: 'Drop (delete) a collection' },
        { value: 'collectionExists', name: 'Collection Exists', description: 'Check if a collection exists' },
        { value: 'getCollectionInfo', name: 'Get Collection Info', description: 'Get collection statistics and indexes' },
      ],
    },
    {
      key: 'collection',
      label: 'Collection',
      type: 'string',
      required: true,
      placeholder: 'users',
      displayOptions: {
        show: {
          operation: ['find', 'findOne', 'insert', 'update', 'delete', 'aggregate', 'bulkInsert', 'createCollection', 'dropCollection', 'collectionExists', 'getCollectionInfo'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getCollections',
          dependsOn: ['database'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'sourceArray',
      label: 'Source Array',
      type: 'string',
      required: true,
      placeholder: '{{ $node["NodeName"].array }}',
      description: 'Drag an array from a previous node. Each item will be inserted as a document.',
      displayOptions: {
        show: {
          operation: ['bulkInsert'],
        },
      },
    },
    {
      key: 'dropIfExists',
      label: 'Drop If Exists',
      type: 'boolean',
      default: false,
      description: 'Drop the collection first if it already exists',
      displayOptions: {
        show: {
          operation: ['createCollection'],
        },
      },
    },
    {
      key: 'query',
      label: 'Query',
      type: 'json',
      placeholder: '{ "status": "active" }',
      displayOptions: {
        show: {
          operation: ['find', 'findOne', 'update', 'delete'],
        },
      },
    },
    {
      key: 'document',
      label: 'Document',
      type: 'json',
      required: true,
      placeholder: '{ "name": "John", "email": "john@example.com" }',
      displayOptions: {
        show: {
          operation: ['insert'],
        },
      },
    },
    {
      key: 'updateData',
      label: 'Update Data',
      type: 'json',
      required: true,
      placeholder: '{ "$set": { "status": "inactive" } }',
      displayOptions: {
        show: {
          operation: ['update'],
        },
      },
    },
    {
      key: 'pipeline',
      label: 'Pipeline',
      type: 'json',
      required: true,
      placeholder: '[{ "$match": { "status": "active" } }]',
      displayOptions: {
        show: {
          operation: ['aggregate'],
        },
      },
    },
  ],
};

export default schema;
